import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Button,
  Row,
  Col,
} from 'reactstrap';

import { ShippingCard } from '../../Delivering/Modals';
import { resolveMarketplaceLogistics } from '../Helper/MarketplacePickupHelper';

// Worded to match what the backend returns for the same condition, so an
// operator who hits the disabled button and an operator who races the AWB
// arriving read the same sentence.
export const MISSING_AWB_MESSAGE = 'Marketplace tracking number is not available yet.';

const CONFIRM_FALLBACK_ERROR = 'Failed to confirm the marketplace pickup. Please try again.';

// The confirm rejects with a specific reason (no AWB yet, not a marketplace
// order, wrong status). MARKETPLACE_PICKUP_SALE_ERROR is handled by no reducer,
// so this is the only place the operator can see it; without it a 422 reads as
// "try again" and retrying always fails.
//
// Both shapes are read because this API answers in two — a bare `message` and a
// nested `error.message`. Reading only the first would swap the specific
// "tracking number is not available yet" reason for the generic fallback, which
// is the one message this modal exists to surface.
//
// Deliberately not `normalizeError`: on an unrecognised body it substitutes its
// own placeholder ("I got something undefined errors") rather than returning
// nothing, and that string would then be shown to a warehouse operator instead
// of the sentence below. Its two-shape reading is what is wanted here, its
// fallback is not.
const confirmError = (err) => {
  const data = err && err.response ? err.response.data : null;
  const message = data && (data.message || (data.error && data.error.message));
  return message || CONFIRM_FALLBACK_ERROR;
};

const LogisticsField = ({ label, value }) => (
  <Col xs={6}>
    <div className="text-muted text-sm">{label}</div>
    <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{value}</div>
  </Col>
);

LogisticsField.propTypes = {
  label: propTypes.string.isRequired,
  value: propTypes.string.isRequired,
};

/**
 * Read-only pickup confirmation for a TikTok/Shopee Pre Delivering sale.
 *
 * Deliberately carries no courier input, courier option or notes: the AWB and
 * the pickup PIN already came from marketplace tracking, and the confirm sends
 * an empty body. Non-marketplace sales keep the courier form — this modal is
 * never rendered for them.
 */
const ModalMarketplacePickup = ({ isOpen, sale, onCancel, onConfirm, onConfirmed, unavailableMessage }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // A modal reopened on another sale must not inherit the previous sale's error.
  // Keyed on the id, not the sale object: a new object identity on every parent
  // render would re-run this effect and wipe an error the operator is reading.
  const saleId = sale ? sale.id : null;
  useEffect(() => {
    if (isOpen) setError(null);
  }, [isOpen, saleId]);

  const logistics = resolveMarketplaceLogistics(sale);

  const handleConfirm = async () => {
    // Guarded as well as disabled: `hasAwb` is the real gate, and the button is
    // only the visible half of it.
    if (isSubmitting || unavailableMessage || !logistics.hasAwb) return;

    setError(null);
    setIsSubmitting(true);
    try {
      const result = await onConfirm(sale.id);
      if (!result || !result.ok) {
        // 422 keeps the sale on the list by design — the AWB simply has not
        // arrived, and it will on a later tracking fetch.
        setError(confirmError(result && result.error));
        return;
      }
      if (onConfirmed) onConfirmed(sale.id);
    } catch (err) {
      console.error(err);
      setError(confirmError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dismissal is blocked while a confirm is in flight. This modal is mounted
  // permanently by the container, so `isSubmitting` and the captured `sale`
  // outlive a close: cancelling mid-request and opening a different sale would
  // render sale B with "Confirming..." and then, when A settles, either paint
  // A's error onto B or fire onConfirmed(A.id) and refetch out from under it.
  const dismiss = isSubmitting ? undefined : onCancel;

  return (
    <Modal isOpen={isOpen} toggle={dismiss} className="modal-dialog modal-md">
      <ModalHeader toggle={dismiss}>Marketplace Pickup</ModalHeader>
      <ModalBody>
        {sale && sale.sale_shipping && (
          <Row>
            <Col sm={12}>
              <ShippingCard
                displayName={sale.sale_product && sale.sale_product.display_name}
                invoiceNumber={sale.invoice_number}
                shipping={sale.sale_shipping}
              />
            </Col>
          </Row>
        )}
        {/* Both gated on `sale`, like ShippingCard above and ModalSubmitCourier:86.
            Closing nulls the sale and clears isOpen in one batch, but Reactstrap
            keeps the body mounted through the fade-out — so an ungated body
            re-renders once with no sale, blanking both fields to '-' and raising
            the missing-AWB warning as the modal fades, including on the success
            path where it reads as a failed confirm. */}
        {sale && (
          <Row className="mt-3">
            <LogisticsField label="AWB" value={logistics.awb} />
            <LogisticsField label="Pickup Code" value={logistics.pickupCode} />
          </Row>
        )}
        {sale && !logistics.hasAwb && (
          <Alert color="warning" className="mt-3 mb-0">
            {MISSING_AWB_MESSAGE}
          </Alert>
        )}
        {sale && unavailableMessage && <Alert color="info" className="mt-3 mb-0">{unavailableMessage}</Alert>}
        {error && (
          <Alert color="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          disabled={isSubmitting || Boolean(unavailableMessage) || !logistics.hasAwb}
          onClick={handleConfirm}
        >
          {isSubmitting ? 'Confirming...' : 'Confirm Pickup'}
        </Button>{' '}
        <Button color="secondary" disabled={isSubmitting} onClick={onCancel}>Cancel</Button>
      </ModalFooter>
    </Modal>
  );
};

ModalMarketplacePickup.propTypes = {
  isOpen: propTypes.bool,
  sale: propTypes.object,
  onCancel: propTypes.func.isRequired,
  onConfirm: propTypes.func.isRequired,
  onConfirmed: propTypes.func,
  unavailableMessage: propTypes.string,
};

ModalMarketplacePickup.defaultProps = {
  isOpen: false,
  sale: null,
};

export default ModalMarketplacePickup;
